import type { PaletteGroup } from '@/lib/protocol-step-draft';
import { foldForSearch } from '@/lib/variable-palette';
import type { BoundRefusal } from '@/lib/variable-pill';
import { boundRefusalSentence } from '@/lib/variable-pill';
import type {
  AnalystLevel,
  AnalystLevelCounts,
  AnalystPass,
  AnalystPassPartKind,
  AnalystSectionGaps,
  NoyauAnalystCall,
  NoyauTrial,
  PlayedProtocol,
  ProtocolAnalyst,
  ProtocolOrphanStep,
  ProtocolOrphanWarning,
  ProtocolStepDraft,
  UsageLine,
  VersionTag,
} from '@matter_extractor/shared';
import {
  ANALYST_FUNCTIONS_READING_TEMPLATE,
  ANALYST_LEVELS,
  ANALYST_LEVEL_LABELS,
  ANALYST_READING_MAX,
  ANALYST_READING_ORPHAN_LABEL,
  ANALYST_STYLE_READING_TEMPLATE,
  ANALYST_STYLE_RUNS_MAX,
  ANALYST_STYLE_RUNS_MIN,
  ANALYST_STYLE_WEIGHT_MAX,
  ANALYST_STYLE_WEIGHT_MIN,
  ANALYST_THRESHOLD_MAX,
  ANALYST_THRESHOLD_MIN,
  analystReadingOrphans,
  assertNever,
  hasAnalystReading,
  protocolOrphanWarnings,
} from '@matter_extractor/shared';

/**
 * Ce que les écrans disent de la section Analyste d'un protocole (analyste.md §3 ;
 * noyau.md §3.7, §4.9, §9.1 ; issues #154, #158 et #196) - les mots et les tris
 * purs, sans un seul composant.
 *
 * Four screens draw the section, and none of them writes a sentence of its own:
 * the editor of a protocol (the levels, the numbers, the two readings and the two
 * panels « Ce que la passe N recevra »), the save box and the pointer box of that
 * protocol (who a version with an incomplete section would stop from starting),
 * the boxes of an experience (`experience-labels.ts`, which borrows the same
 * phrases) and the block « Détermination du noyau » of the parcours (which
 * reading a session was served). What they share lives here, pure and testable
 * without a render - the argument of every `*-labels.ts` of this folder. Since
 * #158, so does what a reading's field offers to cite (its palette, grouped by
 * step) and what the boxes say of an orphan citation (§3.3, N49, N52).
 *
 * What is NOT here is the assembly of the prompt itself, nor the resolution of
 * its citations: the panels render `functionsSystemParts` and `styleSystemParts`
 * of `shared`, the functions the server serves the analyst with (N41, N50), and a
 * second spelling of either in this package would be exactly the divergence that
 * descent exists to forbid.
 */

/** A non-breaking space: what the French typography puts before a colon and a percent sign. */
const NBSP = String.fromCodePoint(0xa0);

/** « 1 objectif », « 2 objectifs » - and « 0 objectif », which French writes in the singular. */
function objectivesCount(count: number): string {
  return `${count} ${count > 1 ? 'objectifs' : 'objectif'}`;
}

/* --------------------------------------------------- the levels of the objectives */

/** The four choices of an objective's level, in the order of the maquette: « Aucun » first. */
export const ANALYST_LEVEL_CHOICES: readonly {
  /** The level, or null for « Aucun ». */
  readonly level: AnalystLevel | null;
  /** The value the radio carries: `none` or the stored level. */
  readonly value: 'none' | AnalystLevel;
  readonly label: string;
}[] = [
  { level: null, value: 'none', label: 'Aucun' },
  ...ANALYST_LEVELS.map((level) => ({
    level,
    value: level,
    label: ANALYST_LEVEL_LABELS[level],
  })),
];

/** One objective of the list of levels: its name as typed - empty while a draft objective is not named yet - and its level. */
export type LevelObjective = {
  readonly id: string;
  readonly name: string;
  readonly level: AnalystLevel | null;
};

/** One step's objectives, under the step's name. */
export type LevelGroup = {
  readonly stepId: string;
  readonly title: string;
  readonly objectives: readonly LevelObjective[];
};

/**
 * EVERY objective of the protocol, grouped by step, in the order of the protocol
 * (§3.5, « les objectifs groupés par étape »): a step with no objective draws no
 * group. A step without a name is « Étape N », as the palette of the readings
 * titles it. Unlike the objectives a reading can CITE, those whose name is not
 * typed yet are listed too: the level is set on the objective, named or not.
 */
export function levelGroups(steps: readonly ProtocolStepDraft[]): LevelGroup[] {
  const groups: LevelGroup[] = [];
  steps.forEach((step, stepIndex) => {
    if (step.objectives.length === 0) return;
    const name = step.name.trim();
    groups.push({
      stepId: step.id,
      title: name === '' ? `Étape ${stepIndex + 1}` : name,
      objectives: step.objectives.map((objective) => ({
        id: objective.id,
        name: objective.name,
        level: objective.analystLevel,
      })),
    });
  });
  return groups;
}

/**
 * The steps with ONE objective's level set (K1). Only the step that carries the
 * objective is rebuilt, and an id no step carries leaves the list as it was, so a
 * click on a row a keystroke just removed writes nothing.
 */
export function withAnalystLevel(
  steps: readonly ProtocolStepDraft[],
  objectiveId: string,
  level: AnalystLevel | null,
): ProtocolStepDraft[] {
  return steps.map((step) =>
    step.objectives.some((objective) => objective.id === objectiveId)
      ? {
          ...step,
          objectives: step.objectives.map((objective) =>
            objective.id === objectiveId ? { ...objective, analystLevel: level } : objective,
          ),
        }
      : step,
  );
}

/**
 * The line of the counts under the levels (the maquette, word for word): « Pourquoi : 2
 * objectifs · Comment : 2 · Quoi : 2 ». The unit « objectifs » follows the FIRST count
 * that is not zero - the maquette of a protocol of before #196 reads « Pourquoi : 0 ·
 * Comment : 0 · Quoi : 0 », and the one with no Comment « Pourquoi : 2 objectifs ·
 * Comment : 0 · Quoi : 2 ».
 */
export function levelCountsLine(counts: AnalystLevelCounts): string {
  const unitAt = ANALYST_LEVELS.findIndex((level) => counts[level] > 0);
  return ANALYST_LEVELS.map((level, at) => {
    const count = counts[level];
    return `${ANALYST_LEVEL_LABELS[level]} : ${at === unitAt ? objectivesCount(count) : count}`;
  }).join(' · ');
}

/** The levels no objective carries, from the counts - in the order of the method. */
export function levelsWithoutObjective(counts: AnalystLevelCounts): AnalystLevel[] {
  return ANALYST_LEVELS.filter((level) => counts[level] === 0);
}

/**
 * What the pastille of the counts says (the maquette): green « chaque niveau a un
 * objectif », or yellow and naming what is missing - « Comment n'a aucun objectif »,
 * « Pourquoi et Quoi n'ont aucun objectif », « aucun niveau n'a d'objectif ».
 */
export function levelsPastille(counts: AnalystLevelCounts): {
  readonly complete: boolean;
  readonly sentence: string;
} {
  const missing = levelsWithoutObjective(counts);
  if (missing.length === 0) return { complete: true, sentence: 'chaque niveau a un objectif' };
  if (missing.length === ANALYST_LEVELS.length) {
    return { complete: false, sentence: 'aucun niveau n’a d’objectif' };
  }
  const subject = missing.map((level) => ANALYST_LEVEL_LABELS[level]).join(' et ');
  return {
    complete: false,
    sentence:
      missing.length === 1 ? `${subject} n’a aucun objectif` : `${subject} n’ont aucun objectif`,
  };
}

/* ------------------------------------------------ what a section lacks, said once */

/**
 * What an incomplete section lacks, in the words the encart of the editor and the
 * lead of the save box share (§3.5, §3.7): « aucun objectif ne porte le niveau
 * Comment », « aucun niveau ne porte d'objectif, et les deux lectures sont
 * vides », « la lecture de la passe 2 est vide ». Empty when the section is
 * complete.
 */
export function analystGapsSentence(gaps: AnalystSectionGaps): string {
  const parts: string[] = [];
  const names = gaps.levelsMissing.map((level) => ANALYST_LEVEL_LABELS[level]);
  if (names.length === ANALYST_LEVELS.length) {
    parts.push('aucun niveau ne porte d’objectif');
  } else if (names.length === 1) {
    parts.push(`aucun objectif ne porte le niveau ${names.join('')}`);
  } else if (names.length > 1) {
    parts.push(`aucun objectif ne porte les niveaux ${names.join(' et ')}`);
  }
  if (gaps.readingsMissing.length === 2) {
    parts.push('les deux lectures sont vides');
  } else if (gaps.readingsMissing[0] !== undefined) {
    parts.push(`la lecture de la passe ${gaps.readingsMissing[0]} est vide`);
  }
  return parts.join(', et ');
}

/**
 * The yellow encart above the levels (the two maquettes of `AnalysteAEtats`): the
 * bold sentence that names what is missing, and - only when the readings are
 * written and a LEVEL is what lacks - the sentence that says what it costs. The
 * maquette of a protocol of before #196, where everything lacks, draws the first
 * alone: the levels are the rule that is not obvious, the readings are.
 */
export function analystEncartSentences(gaps: AnalystSectionGaps): {
  readonly lead: string;
  readonly rest: string;
} {
  const levelsOnly = gaps.levelsMissing.length > 0 && gaps.readingsMissing.length === 0;
  return {
    lead: `La section n’est pas complète : ${analystGapsSentence(gaps)}.`,
    rest: levelsOnly
      ? 'Une expérience qui joue la séquence du noyau avec ce protocole ne démarrera pas tant que chaque niveau n’aura pas un objectif.'
      : '',
  };
}

/* ---------------------------------------------------- the three numbers of §3.2 */

/** The four numbers the section sets (§3.2, #229). */
export type AnalystNumberField = keyof Pick<
  ProtocolAnalyst,
  'pairThreshold' | 'soloThreshold' | 'styleWeight' | 'styleRuns'
>;

/** The bounds of each: 51 to 100 for a threshold (K2), 0 to 100 for the weight of the style, 1 to 5 for its readings (#229). */
export const ANALYST_NUMBER_BOUNDS: Readonly<
  Record<AnalystNumberField, { readonly min: number; readonly max: number }>
> = {
  pairThreshold: { min: ANALYST_THRESHOLD_MIN, max: ANALYST_THRESHOLD_MAX },
  soloThreshold: { min: ANALYST_THRESHOLD_MIN, max: ANALYST_THRESHOLD_MAX },
  styleWeight: { min: ANALYST_STYLE_WEIGHT_MIN, max: ANALYST_STYLE_WEIGHT_MAX },
  styleRuns: { min: ANALYST_STYLE_RUNS_MIN, max: ANALYST_STYLE_RUNS_MAX },
};

/**
 * What a field typed reads as, or null: a whole number inside the bounds of the
 * field. The draft only ever holds such a number - an autosave that sent 7 on the
 * way to 70 would be a 400, the one answer an autosave must not get - so the
 * field keeps what is typed and the draft moves when it parses.
 */
export function analystNumberOf(field: AnalystNumberField, typed: string): number | null {
  const text = typed.trim();
  if (!/^\d{1,3}$/.test(text)) return null;
  const value = Number(text);
  const { min, max } = ANALYST_NUMBER_BOUNDS[field];
  return value >= min && value <= max ? value : null;
}

/** The refusal said under a field whose text does not read (§3.2): the bounds, in words. */
export function analystNumberRefusal(field: AnalystNumberField): string {
  const { min, max } = ANALYST_NUMBER_BOUNDS[field];
  return `Un nombre entier de ${min} à ${max}.`;
}

/**
 * The line under « Poids du style » (the maquette, word for word, whatever the
 * weight): what each reading counts for in the note of a candidate (§8.1).
 */
export function styleWeightHint(weight: number): string {
  return `Note d’un candidat${NBSP}: ${100 - weight}${NBSP}% de compatibilité, ${weight}${NBSP}% de style.`;
}

/** « lecture » at 1, « lectures » beyond: the unit beside « Lectures du style » (§3.5, #229). */
export function styleRunsUnit(runs: number): string {
  return runs === 1 ? 'lecture' : 'lectures';
}

/**
 * The line under « Lectures du style », written from the value so the field and the sentence cannot
 * say two things (§3.5, planche `LecturesSection`): « Le style d'un candidat est la moyenne de 3
 * lectures : la passe 2 coûte 3 fois plus. », and at 1 « Une seule lecture du style. ».
 */
export function styleRunsHint(runs: number): string {
  return runs === 1
    ? 'Une seule lecture du style.'
    : `Le style d’un candidat est la moyenne de ${runs} lectures : la passe 2 coûte ${runs} fois plus.`;
}

/* ------------------------------------------------------- the two readings (§3.3) */

/** The tab of each pass, middle dot included (the maquette). */
export const ANALYST_PASS_TAB: Readonly<Record<AnalystPass, string>> = {
  1: 'Passe 1 · les fonctions',
  2: 'Passe 2 · le style',
};

/** The label of the reading each tab shows. */
export const ANALYST_PASS_READING_LABEL: Readonly<Record<AnalystPass, string>> = {
  1: 'Lecture de la passe 1',
  2: 'Lecture de la passe 2',
};

/**
 * The first sentence of the guide above each reading, which is also the
 * placeholder of the empty field: what that pass reads and so what its reading
 * is for (§3.3). « Tape @ pour citer un objectif : la passe lira son nom. »
 * follows it, set in the component where the `@` is in bold.
 */
export const ANALYST_PASS_GUIDE: Readonly<Record<AnalystPass, string>> = {
  1: 'Dis comment lire les extraits de ce protocole, objectif par objectif.',
  2: 'Dis comment lire le discours entier de la personne dans ce protocole.',
};

/** What a reading is born as, for the line « Lecture générique, préremplie » (K15). */
const GENERIC_READING: Readonly<Record<AnalystPass, string>> = {
  1: ANALYST_FUNCTIONS_READING_TEMPLATE,
  2: ANALYST_STYLE_READING_TEMPLATE,
};

/**
 * Is this reading the generic one of its pass, as a protocol created since #196 is
 * born with it (P11, K15)? What puts « Lecture générique, préremplie » under the
 * field: compared after a trim, the way the save stores it.
 */
export function isGenericPassReading(pass: AnalystPass, reading: string): boolean {
  return reading.trim() === GENERIC_READING[pass];
}

/**
 * What the palette says above its greyed lines: the field is bounded at 4000
 * characters of STORED text (N31), a citation weighs its token there, and one
 * that would pass the bound is refused, never cut to fit (review of !478).
 */
export const READING_PALETTE_FULL = `Plus de place pour une citation : la lecture dépasserait ses ${ANALYST_READING_MAX} caractères. Raccourcissez-la pour citer un objectif.`;

/**
 * What the `<output>` (role `status`) under the field says when its bound
 * refuses an input (second review of !478): that NOTHING was added - a paste
 * is never cut to fit -, what it would have taken, and the room left. The
 * count beside the field is hidden from a screen reader, so this is the one
 * place a refusal is said at all: a paste that did nothing, without a word,
 * reads as a broken field. The sentence is every bounded field's since #160,
 * the reading naming itself where a counted zone says « le champ ».
 */
export function readingRefusalSentence(refusal: BoundRefusal): string {
  return boundRefusalSentence(refusal, ANALYST_READING_MAX, 'la lecture');
}

/**
 * The heading of each part in the panels - interface words, and NOT the `##`
 * lines of the prompt (README of the maquettes): the framing has no heading at
 * all in the prompt, and « ## Ce que tu rends » would read as an instruction to
 * the operator. The method part and the reading are named by the pass.
 */
export const ANALYST_PASS_PART_LABEL: Readonly<
  Record<AnalystPass, Readonly<Record<AnalystPassPartKind, string>>>
> = {
  1: {
    framing: 'Le cadre',
    method: 'Les trois fonctions',
    reading: 'Comment lire la matière',
    contract: 'Ce que tu rends',
  },
  2: {
    framing: 'Le cadre',
    method: 'Les six styles',
    reading: 'Comment lire le discours',
    contract: 'Ce que tu rends',
  },
};

/** The two buttons that unfold and fold the part Méthode wrote, which keeps a preview when folded. */
export const ANALYST_PASS_METHOD_TOGGLE: Readonly<
  Record<AnalystPass, { readonly open: string; readonly close: string }>
> = {
  1: { open: 'Déplier les fonctions', close: 'Replier les fonctions' },
  2: { open: 'Déplier les styles', close: 'Replier les styles' },
};

/** The line under each panel: what the MESSAGE of that pass carries, which no panel can show (§3.4). */
export const ANALYST_PASS_MESSAGE_LINE: Readonly<Record<AnalystPass, string>> = {
  1: 'Le message porte la matière : les extraits des objectifs à niveau, sous leur nom, sans leur niveau.',
  2: 'Le message porte les candidats, puis toutes les réponses de la personne, numérotées par tour, sans les messages de l’IA.',
};

/** The badge of the part written in Réglages > Méthode: its version, the « latest dev » (§3.4). */
export function methodBadge(versionNumber: number | null): string {
  return versionNumber === null ? 'Réglages > Méthode' : `Réglages > Méthode v${versionNumber}`;
}

/**
 * What the part stands for while Méthode is read, when it has no version on this side
 * (§4.5), and when the read failed. The last says that the READ failed and not that
 * Méthode is absent, which nothing has told the panel; a reload reads it again.
 */
export const METHOD_READING_BODY = 'Lecture de Méthode…';
export const METHOD_MISSING_BODY =
  'Méthode n’a encore aucune version de ce côté : le prompt de cette passe ne peut pas être assemblé.';
export const METHOD_FAILED_BODY =
  'Méthode n’a pas pu être lue : le prompt de cette passe ne peut pas être assemblé. Rechargez l’écran.';

/** The grey block of the reading of before #196 (§3.8), its title and its button. */
export const PREVIOUS_READING_TITLE = 'Lecture d’avant #196 (plus servie)';
export const PREVIOUS_READING_TAKE = 'Reprendre dans la passe 1';
export const PREVIOUS_READING_TAKE_HELP =
  'Copie ce texte dans le brouillon, à la place de la lecture de la passe 1.';

/**
 * One line of a part as the panel draws it, with `at`, its line number in the
 * body: unique and stable while the text does not change, which is what a
 * React key needs - the rank in the drawn list would not be (a gap dropped
 * above renumbers everything below it).
 */
export type ReadingLine =
  | { readonly kind: 'text'; readonly at: number; readonly text: string }
  | { readonly kind: 'bullet'; readonly at: number; readonly text: string }
  | { readonly kind: 'gap'; readonly at: number };

/**
 * The body of one of the parts WRITTEN IN CODE or by Méthode cut into the lines
 * the panel draws: a line opening on « - » is a bullet with a hanging indent, an
 * empty line is a gap between paragraphs, anything else a line of text kept as
 * written - leading spaces included, which is what keeps the JSON template of the
 * contract readable. Gaps are never doubled nor left at either end.
 *
 * Never the protocol's own reading: that one is drawn exactly as served, blank
 * lines and trailing spaces included (N41, review of !474) - tidying what
 * somebody typed would show a reading the analyst never receives. And NOTHING
 * is interpreted beyond the bullets - no bold, no heading, no link: the text
 * goes to a model verbatim, and a panel that rendered markup the analyst reads
 * as characters would show something it does not receive.
 */
export function readingLines(body: string): ReadingLine[] {
  const lines: ReadingLine[] = [];
  body.split('\n').forEach((raw, at) => {
    const line = raw.replace(/\s+$/, '');
    if (line === '') {
      const last = lines.at(-1);
      if (last !== undefined && last.kind !== 'gap') lines.push({ kind: 'gap', at });
      return;
    }
    const bullet = /^- (.*)$/.exec(line);
    lines.push(
      bullet === null
        ? { kind: 'text', at, text: line }
        : { kind: 'bullet', at, text: bullet[1] ?? '' },
    );
  });
  if (lines.at(-1)?.kind === 'gap') lines.pop();
  return lines;
}

/* ------------------------------------ the objectives cited by `@`, #158 */

/** The palette of a protocol with no NAMED objective yet (N49). */
export const READING_PALETTE_NONE = 'Aucun objectif à citer pour l’instant';

/**
 * The palette of the readings (§3.3, N49): EVERY named objective of the
 * protocol, grouped by step - the title of a group is the name of the step -,
 * with no rule of reach, since a reading is worth the whole protocol and is
 * served after its close, and with NO criterion, the analyst seeing objectives
 * only (N48). A step with no named objective, or none that matches the letters
 * typed after the `@`, draws no group.
 *
 * The line and its meta are those of the directions' palette, whose component
 * draws this one (W1): the chip, then the obligation - the step is the title
 * already. A homonym inside ONE step is inked, the only case the title does
 * not tell apart. The search folds case and accents, as the palette of an
 * experience does. The consigne of the rangeur offers the same list.
 */
export function readingPaletteGroups(
  steps: readonly ProtocolStepDraft[],
  query: string,
): PaletteGroup[] {
  const wanted = foldForSearch(query.trim());
  const groups: PaletteGroup[] = [];
  steps.forEach((step, stepIndex) => {
    const named = step.objectives.filter((objective) => objective.name.trim() !== '');
    const counts = new Map<string, number>();
    for (const objective of named) {
      const key = foldForSearch(objective.name.trim());
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const items = named
      .filter((objective) => wanted === '' || foldForSearch(objective.name).includes(wanted))
      .map((objective) => ({
        variable: { kind: 'objective', id: objective.id } as const,
        label: objective.name,
        meta: objective.mandatory ? 'obligatoire' : 'optionnel',
        duplicate: (counts.get(foldForSearch(objective.name.trim())) ?? 0) > 1,
      }));
    if (items.length === 0) return;
    const name = step.name.trim();
    groups.push({ id: step.id, title: name === '' ? `Étape ${stepIndex + 1}` : name, items });
  });
  return groups;
}

/**
 * What the palette says when it offers nothing: the protocol has no named
 * objective yet (N49), or none carries the letters typed after the `@` - an
 * empty menu under « @Sit » must not claim there is nothing to cite at all.
 */
export function readingPaletteEmpty(steps: readonly ProtocolStepDraft[], query: string): string {
  const typed = query.trim();
  const anyNamed = steps.some((step) =>
    step.objectives.some((objective) => objective.name.trim() !== ''),
  );
  return anyNamed && typed !== ''
    ? `Aucun objectif ne correspond à « ${typed} »`
    : READING_PALETTE_NONE;
}

/**
 * The yellow line of an orphan in a READING (§3.3, V2, V3): what the analyst will
 * read in its place, the label of `shared`, and the pass that reads it.
 */
export function readingOrphanWarning(pass: AnalystPass): string {
  return `La lecture de la passe ${pass} cite un objectif qui n’existe plus dans cette version : l’analyste lira « ${ANALYST_READING_ORPHAN_LABEL} » à sa place.`;
}

/**
 * The yellow line of an orphan in the CONSIGNE of the rangement (scenario-succes.md
 * §4, #177): the same régime as the readings, and the same label.
 */
export const RANGEMENT_ORPHAN_WARNING = `La consigne du rangeur cite un objectif qui n’existe plus dans cette version : le rangeur lira « ${ANALYST_READING_ORPHAN_LABEL} » à sa place.`;

/**
 * The yellow line of an orphan in the DIRECTIONS of one step (§8.6 of the
 * refonte, V5, N52; the maquette word for word): the step's own direction, an
 * objective's or an action's, the step being the unit the boxes speak in.
 */
export function directionOrphanWarning(
  step: Pick<ProtocolOrphanStep, 'stepIndex' | 'stepName'>,
): string {
  const name = step.stepName.trim();
  const where = name === '' ? `l’étape ${step.stepIndex + 1}` : `l’étape « ${name} »`;
  return `La direction de ${where} cite un objectif qui n’existe plus dans cette version.`;
}

/**
 * An orphan chip of a version the boxes of the protocol warn about (N52): a reading
 * of the section, per pass - since #196 -, the directions of a step, or the consigne
 * of the rangement. The reading of BEFORE #196 is not among them: it is neither
 * served nor edited any more (K14), so what it cites warns nobody.
 */
export type EditorOrphanWarning =
  | Exclude<ProtocolOrphanWarning, { readonly kind: 'reading-variable-orphan' }>
  | { readonly kind: 'analyst-reading-orphan'; readonly pass: AnalystPass };

/**
 * The orphan chips of a content, in the order the boxes list them: the reading of
 * the pass 1, the one of the pass 2, each step whose directions cite a ghost, then
 * the consigne of the rangement. `protocolOrphanWarnings` is handed an empty
 * reading ON PURPOSE - the old one is not served - and `shared` keeps the walk of
 * the directions and of the consigne.
 */
export function sectionOrphanWarnings(
  steps: readonly ProtocolStepDraft[],
  analyst: Pick<ProtocolAnalyst, 'functionsReading' | 'styleReading'>,
  rangementConsigne: string,
): EditorOrphanWarning[] {
  const readings = analystReadingOrphans(steps, analyst);
  const others = protocolOrphanWarnings(steps, '', rangementConsigne).flatMap(
    (warning): EditorOrphanWarning[] =>
      warning.kind === 'reading-variable-orphan' ? [] : [warning],
  );
  return [
    ...(readings.functionsReading
      ? [{ kind: 'analyst-reading-orphan' as const, pass: 1 as const }]
      : []),
    ...(readings.styleReading
      ? [{ kind: 'analyst-reading-orphan' as const, pass: 2 as const }]
      : []),
    ...others,
  ];
}

/** One orphan chip of a protocol's box, in words (N52) - never a refusal. */
export function orphanWarningSentence(warning: EditorOrphanWarning): string {
  switch (warning.kind) {
    case 'analyst-reading-orphan':
      return readingOrphanWarning(warning.pass);
    case 'direction-variable-orphan':
      return directionOrphanWarning(warning);
    case 'rangement-variable-orphan':
      return RANGEMENT_ORPHAN_WARNING;
    default:
      return assertNever(warning, 'orphanWarningSentence');
  }
}

/* ------------------------------------------------- the protocol side, N37 */

/**
 * The experiences a gesture on the protocol would stop from starting (§3.7,
 * N37): those whose tagged version plays the noyau sequence with this
 * protocol cited under one of `tags` - the tags the gesture MOVES,
 * `development` for a save, `production` for « Passer en production » or the
 * ticked box, the target's tag for a move from the history.
 *
 * `playsNoyauAt` is computed by the server (it alone reads the referents'
 * versions) and ABSENT means none: a line built by a reader that knows nothing
 * of the noyau names nobody. Each experience once, in the order of the usage.
 */
export function experiencesPlayingNoyauAt(
  usage: readonly UsageLine[],
  tags: readonly VersionTag[],
): UsageLine[] {
  return usage.filter(
    (line) =>
      line.kind === 'experience' && (line.playsNoyauAt ?? []).some((tag) => tags.includes(tag)),
  );
}

/**
 * The title of the yellow encart - the source's own words, which the README of
 * the maquettes keeps over the planche's: one warning per experience named.
 */
export function readingWarningTitle(count: number): string {
  return `${count} ${count === 1 ? 'avertissement' : 'avertissements'}, jamais un refus.`;
}

/**
 * What the save box of a protocol says first, once (§3.7): that the section of
 * THIS version is incomplete, and what it lacks - the words of the encart of the
 * editor, so a box and the screen it points at never name two things.
 */
export function sectionIncompleteLead(gaps: AnalystSectionGaps): string {
  return `La section Analyste de cette version n’est pas complète : ${analystGapsSentence(gaps)}.`;
}

/** One experience a SAVE would stop from starting - the sentence of §3.7, word for word. */
export function saveSectionSentence(experienceName: string): string {
  return `L’expérience « ${experienceName} » joue la séquence du noyau avec ce protocole : ses sessions ne démarreront plus.`;
}

/**
 * One experience a POINTER move would stop from starting, set under the row of
 * the experience that will play the version (§3.7): « Passer en production »,
 * a rollback from the history, and « Reculer développement », whose target is
 * as capable as any other version of having an incomplete section.
 */
export function pointerSectionSentence(experienceName: string): string {
  return `L’expérience « ${experienceName} » joue la séquence du noyau : avec une section Analyste incomplète, ses sessions ne démarreront plus.`;
}

/* ------------------------------------------------- the parcours, §9.1 */

/**
 * The reading a determination was served: the frozen protocol's own, or the
 * generic one (N38) - the protocol and its version then being what the line
 * links to, at the reading of that frozen version.
 */
export type ServedReading =
  | {
      readonly kind: 'protocol';
      readonly protocolId: string;
      readonly versionId: string;
      readonly name: string;
      readonly versionNumber: number;
    }
  | { readonly kind: 'generic' };

/** The line « Lecture : ... · prompt ... · empreinte ... » of the block (§9.1, N40). */
export type AnalystReadingFacts = {
  /** Null when the frozen protocol does not read back - the line then says no reading. */
  readonly reading: ServedReading | null;
  /** The version of the assembly written in code (`2` since #154), null before the field existed. */
  readonly promptVersion: string | null;
  /** The fingerprint of the system served (N39), null on a call recorded before #154. */
  readonly fingerprint: string | null;
};

/**
 * What the block « Détermination du noyau » says of the reading a session was
 * served (§9.1, N38, N40) - or null when no call was made: on `pending` or on
 * `no-extracts` nothing has been served yet, or ever, and a line naming an
 * instrument no call received would describe an event that did not happen.
 *
 * The prompt version and the fingerprint are the LAST call's, the régime of
 * `list_sessions` (N40): the retry keeps the system, so a determination's
 * calls share one fingerprint, and a failure lists each attempt with its own
 * beside this line anyway.
 *
 * « Générique » when the frozen protocol carries no reading, which one kind of
 * session reaches: one started before #154, whose protocol was written before
 * the field existed (N38) - the refusal at start-up keeps every later session
 * from getting there. Its calls, recorded before the fingerprint existed, read
 * with a null one, and the line says « sans empreinte » rather than inventing a
 * value (the decision of lot 7, documented in `walk-noyau-block.tsx`).
 */
export function analystReadingFacts(
  protocol: PlayedProtocol | null,
  calls: readonly NoyauAnalystCall[],
): AnalystReadingFacts | null {
  const last = calls.at(-1);
  if (last === undefined) return null;
  const reading: ServedReading | null =
    protocol === null
      ? null
      : hasAnalystReading(protocol.content.analystReading)
        ? {
            kind: 'protocol',
            protocolId: protocol.id,
            versionId: protocol.versionId,
            name: protocol.name,
            versionNumber: protocol.versionNumber,
          }
        : { kind: 'generic' };
  return { reading, promptVersion: last.promptVersion, fingerprint: last.promptFingerprint };
}

/**
 * The same line for a TRIAL of the analyst (noyau.md §9.4, #164): the version
 * of the protocol it was served the reading of - its development version when
 * the button was pressed, never the frozen one - and the last call's prompt and
 * fingerprint. Null until a call came back, for the reason given above. Never
 * « générique »: a trial is refused on a version with no reading.
 */
export function trialReadingFacts(trial: NoyauTrial): AnalystReadingFacts | null {
  const last = trial.determination.calls.at(-1);
  if (last === undefined) return null;
  return {
    reading: {
      kind: 'protocol',
      protocolId: trial.reading.protocolId,
      versionId: trial.reading.versionId,
      name: trial.reading.name,
      versionNumber: trial.reading.versionNumber,
    },
    promptVersion: last.promptVersion,
    fingerprint: last.promptFingerprint,
  };
}
